import { useEffect } from "react";
import "@/App.css";
import SiteHeader from "@/components/SiteHeader";
import { motion } from "framer-motion";
import { useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ArrowLeft, Mail, Phone, MapPin } from "lucide-react";

const JANE_URL = "https://hayahealingpsychotherapy.janeapp.com/";

const ease = [0.22, 1, 0.36, 1];
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.05 + i * 0.08, ease } }),
};

const Reveal = ({ i = 0, className = "", children, ...rest }) => (
  <motion.div variants={fadeUp} custom={i} initial="hidden" whileInView="visible"
    viewport={{ once: true, margin: "-60px" }} className={className} {...rest}>
    {children}
  </motion.div>
);

export const CONCERNS = {
  "individual-therapy": {
    title: "Individual Psychotherapy",
    intro: [
      "One-to-one psychotherapy for adults navigating anxiety, stress, low mood, identity concerns, and life transitions.",
      "Sessions provide a structured, confidential space to understand patterns of thinking, feeling, and behaviour, and to develop alternative ways of responding.",
    ],
    bullets: ["Anxiety", "Depression", "Trauma", "ADHD", "Grief", "Relationship patterns", "Stress & burnout", "Life transitions"],
  },
  "child-therapy": {
    title: "Child Psychotherapy",
    intro: [
      "Play-based, developmentally tailored psychotherapy that helps children name, understand, and move through big feelings.",
      "Work with children is collaborative with parents, with treatment goals reviewed and adjusted as your child grows.",
    ],
    bullets: ["Big feelings and emotional regulation", "Anxiety and worries", "Behavioural concerns", "Social skills", "School difficulties", "Family changes and transitions"],
  },
  "teen-therapy": {
    title: "Teen Psychotherapy",
    intro: [
      "An honest, pressure-free space for teens to navigate identity, school stress, friendships, and emotional change.",
      "Sessions balance confidentiality with appropriate parent involvement, and move at the pace the teen can hold.",
    ],
    bullets: ["Anxiety and low mood", "Identity and self-esteem", "Academic pressure", "Peer and friendship stress", "Family conflict", "Emotional regulation"],
  },
  "family-counselling": {
    title: "Family Therapy",
    intro: [
      "Structured family sessions that strengthen communication, repair ruptures, and rebuild trust across the family system.",
      "Work considers each member's perspective — including cultural and intergenerational context — rather than treating any one person as the problem.",
    ],
    bullets: ["Communication breakdown", "Recurring conflict", "Parenting disagreements", "Blended family transitions", "Boundaries within the family", "Reconnection after rupture"],
  },
  "emotional-regulation": {
    title: "Emotional Regulation",
    intro: [
      "Psychotherapy for difficulty managing emotions — intense emotional responses, coping difficulties, and patterns of avoidance or reactivity.",
      "Sessions build the skills to notice, name, and influence emotional responses before they take over, drawing on DBT- and ACT-informed strategies.",
    ],
    bullets: ["Intense emotional responses", "Coping difficulties", "Patterns of avoidance or reactivity", "Emotional overwhelm", "Stress responses", "Building self-soothing skills"],
  },
  "womens-issues": {
    title: "Women's Mental Health",
    intro: [
      "Focused psychotherapy for the identity shifts, role strain, and expectations many women carry — often quietly.",
      "Sessions address both the immediate concern and the patterns and contexts that sustain it.",
    ],
    bullets: ["Identity and self-worth", "Burnout and role strain", "Motherhood transitions", "Boundaries", "Anxiety and mood", "Cultural and family expectations"],
  },
  "prenatal-postpartum": {
    title: "Pre-natal & Postpartum Therapy",
    intro: [
      "Compassionate, evidence-based support through pregnancy, birth, postpartum adjustment, and early parenthood.",
      "Treatment may address mood and anxiety changes, identity shifts, and the relational impact of becoming a parent.",
    ],
    bullets: ["Pregnancy anxiety", "Postpartum mood changes", "Identity shifts", "Relationship changes", "Birth experiences", "Support systems and expectations"],
  },
  "divorce-separation": {
    title: "Divorce & Separation Support",
    intro: [
      "Steady, structured support through separation — the grief, the logistics, and the slow work of rebuilding.",
      "Work may include co-parenting communication, supporting children through the transition, and re-establishing your sense of self.",
    ],
    bullets: ["Grief and loss", "Co-parenting", "Rebuilding identity", "Communication with a former partner", "Children through transition", "Decision-making under stress"],
  },
  "toxic-family-dynamics": {
    title: "Toxic Family Dynamics",
    intro: [
      "Psychotherapy for adults navigating harmful or painful family patterns — including those shaped by culture, obligation, and intergenerational history.",
      "Treatment focuses on understanding these patterns, establishing protective boundaries, and rebuilding emotional autonomy.",
    ],
    bullets: ["Intergenerational patterns", "Boundaries with family", "Guilt and obligation", "Emotional autonomy", "Cultural expectations", "Estrangement decisions"],
  },
  "parent-support-coaching": {
    title: "Parent Support & Coaching",
    intro: [
      "Practical, compassionate coaching for parents — grounded in child development and evidence-based practice.",
      "Sessions focus on concrete strategies that fit your family, your culture, and your child's needs.",
    ],
    bullets: ["Emotional attunement", "Discipline without conflict", "Co-parenting alignment", "Parenting neurodivergent children", "Managing parental stress", "Family routines"],
  },
  "menopause-perimenopause": {
    title: "Menopause & Perimenopause Psychotherapy",
    intro: [
      "Psychotherapy for the emotional, cognitive, and identity shifts that can accompany perimenopause and menopause.",
      "Sessions address mood, anxiety, sleep, self-image, and relationship changes within a broader clinical picture.",
    ],
    bullets: ["Mood and anxiety changes", "Identity and self-image", "Sleep and energy", "Relationship impact", "Cognitive changes", "Grief and transition"],
  },
  "relationships-dating": {
    title: "Relationship & Dating Psychotherapy",
    intro: [
      "Individual psychotherapy for the patterns that show up in your relationships — dating, attachment, breakups, and the space in between.",
      "Sessions focus on understanding how you relate, what you repeat, and how to build connections that feel secure.",
    ],
    bullets: ["Dating anxiety and patterns", "Attachment styles", "Breakups and heartache", "Choosing partners", "Boundaries in early relationships", "Rebuilding after a breakup"],
  },
  "trauma-therapy": {
    title: "Trauma Psychotherapy",
    intro: [
      "Structured, trauma-informed psychotherapy for adults carrying the effects of difficult or overwhelming experiences.",
      "Treatment draws on validated approaches — including Cognitive Processing Therapy — paced to what your nervous system can hold.",
    ],
    bullets: ["Childhood experiences", "Sexual violence and abuse", "Domestic abuse", "PTSD symptoms", "Complex trauma", "Feeling stuck in survival mode"],
  },
  "depression": {
    title: "Psychotherapy for Depression",
    intro: [
      "Evidence-based psychotherapy for low mood, numbness, and the loss of motivation or meaning that depression brings.",
      "Treatment is structured and measurable — built around behavioural activation, cognitive work, and the contexts sustaining the low mood.",
    ],
    bullets: ["Persistent low mood", "Loss of interest or pleasure", "Fatigue and low motivation", "Negative self-talk", "Withdrawal from relationships", "Functioning while struggling"],
  },
  "anxiety": {
    title: "Psychotherapy for Anxiety",
    intro: [
      "Structured, evidence-based treatment for anxiety — from constant worry to panic and social anxiety.",
      "Sessions combine cognitive-behavioural and acceptance-based tools with a clear understanding of what is driving the anxiety.",
    ],
    bullets: ["Constant worry", "Panic attacks", "Social anxiety", "Overthinking and rumination", "Physical symptoms of anxiety", "Performance and work anxiety"],
  },
  "premarital-counselling": {
    title: "Premarital Counselling",
    intro: [
      "Structured preparation for marriage — for couples who want to begin on solid ground.",
      "Sessions cover communication, conflict, finances, family expectations, intimacy, and faith — including the particular dynamics of South Asian and intercultural marriages.",
    ],
    bullets: ["Communication foundations", "Conflict and repair skills", "Family and in-law expectations", "Finances and roles", "Intimacy and affection", "Faith and shared values"],
  },
  "adhd": {
    title: "Psychotherapy for ADHD",
    intro: [
      "Psychotherapy for adults and teens with ADHD — practical, structured, and shame-free.",
      "Treatment focuses on executive function, emotional regulation, and the self-criticism that often builds up around ADHD.",
    ],
    bullets: ["Focus and follow-through", "Executive function strategies", "Emotional regulation", "Procrastination and overwhelm", "Shame and self-criticism", "School and work demands"],
  },
  "grief-counselling": {
    title: "Grief Counselling",
    intro: [
      "Steady, compassionate psychotherapy for loss — of a person, a relationship, a pregnancy, or a life you expected.",
      "There is no fixed timeline for grief; sessions make room for its weight while helping daily life keep moving.",
    ],
    bullets: ["Death of a loved one", "Pregnancy loss", "Loss of a relationship", "Anticipatory grief", "Complicated grief", "Grief across cultures and faith"],
  },
  "south-asian-womens-therapy": {
    title: "South Asian Women's Psychotherapy",
    intro: [
      "Psychotherapy for South Asian women navigating the particular weight of cultural expectation — eldest-daughter dynamics, family obligation, identity, and self-worth.",
      "Sessions in English or Urdu with a practice that understands these dynamics from the inside — no cultural explaining required.",
    ],
    bullets: ["Eldest daughter dynamics", "Family obligation and guilt", "Identity between cultures", "Boundaries without shame", "Marriage and role expectations", "Self-worth and perfectionism"],
  },
  "faith-based-therapy": {
    title: "Faith-Based Psychotherapy",
    intro: [
      "Psychotherapy that respects and integrates your faith — for clients who want their spiritual life included in the clinical work, not set apart from it.",
      "Sessions remain grounded in evidence-based practice while drawing on the beliefs, practices, and community that anchor you.",
    ],
    bullets: ["Integrating faith and mental health", "Religious guilt and scrupulosity", "Spiritual identity", "Faith and family expectations", "Grief and meaning through faith", "Values-led decision making"],
  },
};

export const DEFAULT_BENEFITS = [
  ["Know Your Own Patterns", "Explore the history, beliefs, and habits shaping how you feel and respond."],
  ["Work Through Old Wounds", "Process difficult experiences safely, at a pace your nervous system can hold."],
  ["Change How You Relate", "Strengthen communication, boundaries, and connection with the people who matter."],
];

export const BENEFITS = {
  "individual-therapy": [
    ["Know Your Own Patterns", "Gain clinical insight into the habits, beliefs, and experiences shaping how you see yourself and respond to others."],
    ["Reduce Anxiety, Low Mood & Stress", "Learn structured, evidence-based tools — grounded in CBT and ACT — to ease symptoms and function better day to day."],
    ["Work Through Old Wounds", "Process difficult experiences safely with validated trauma-informed approaches, at a pace your nervous system can hold."],
    ["Make Room for Grief", "Give loss — of a person, a role, or a life you expected — the space it deserves, without judgment or a timeline."],
    ["Change How You Relate", "Strengthen boundaries, communication, and the ability to spot patterns that no longer serve you."],
    ["Work With Your ADHD Brain", "Understand how your mind works and build practical strategies for focus, follow-through, and overwhelm."],
  ],
  "family-counselling": [
    ["Communicate Without Escalation", "Learn to raise hard topics without conversations turning into conflict."],
    ["Repair Trust After Rupture", "Rebuild safety and reliability between family members, step by step."],
    ["Function as a Team Again", "Rebuild shared goals and the sense that you're facing problems together, not against each other."],
    ["Understand What's Driving the Conflict", "Work with the patterns underneath the recurring arguments — not just the argument of the week."],
    ["Restore Calm and Safety at Home", "Put routines and boundaries in place that every member of the household can rely on."],
  ],
  "child-therapy": [
    ["Help Your Child Name Big Feelings", "Play-based work that gives children words and tools for what's inside."],
    ["Build Emotional Regulation Early", "Skills for managing frustration, worry, and overwhelm before they grow."],
    ["Support Confidence at School and Home", "Address social, behavioural, and learning-related stress in context."],
  ],
  "teen-therapy": [
    ["A Space That's Theirs", "Confidential, pressure-free sessions where teens set the pace."],
    ["Tools for Anxiety and Pressure", "Practical strategies for school stress, friendships, and self-criticism."],
    ["Stronger Family Communication", "Bridge the gap between teens and parents without taking sides."],
  ],
  "womens-issues": [
    ["Reconnect With Your Own Needs", "Make room for yourself alongside the roles you carry for others."],
    ["Set Boundaries Without Guilt", "Practise saying no in ways that hold, even with family."],
    ["Navigate Roles and Expectations", "Work through the cultural and family pressures shaping your choices."],
  ],
  "prenatal-postpartum": [
    ["Steady Support Through the Transition", "Care for mood, anxiety, and identity through pregnancy and after birth."],
    ["Care for Your Energy and Mind", "Address sleep, overwhelm, and the mental load of early parenthood."],
    ["Adjust Together", "Support for the relationship shifts that come with a new baby."],
  ],
  "divorce-separation": [
    ["Grieve and Rebuild", "Process the loss of the relationship while keeping daily life steady."],
    ["Co-Parent With Less Conflict", "Build communication that protects your children from the middle."],
    ["Rediscover Your Footing", "Re-establish identity, routine, and direction after separation."],
  ],
  "toxic-family-dynamics": [
    ["Understand the Pattern", "See how family dynamics formed and why they keep repeating."],
    ["Set Protective Boundaries", "Learn to protect your wellbeing without cutting yourself off from care."],
    ["Reclaim Emotional Autonomy", "Loosen guilt and obligation so your decisions become your own."],
  ],
  "parent-support-coaching": [
    ["Concrete Strategies That Fit", "Practical tools matched to your child, your culture, and your home."],
    ["Less Conflict, More Connection", "Approaches to discipline and routines that lower the temperature."],
    ["Confidence in Your Parenting", "Replace second-guessing with a clear, workable approach."],
  ],
  "menopause-perimenopause": [
    ["Steady Mood and Self-Image", "Address anxiety, irritability, and identity shifts with clinical care."],
    ["Make Sense of the Changes", "Understand what's hormonal, what's situational, and what to do about both."],
    ["Support for Relationships", "Navigate the impact on partnership, family, and work."],
  ],
};

const AUDIENCE_NOTES = {
  "teen-therapy": [
    ["If you're a teen reading this", "Therapy is confidential — what you say stays private, and you decide how involved your parents are. Safety is the only exception, and your therapist will always talk with you first."],
    ["If you're a parent reading this", "Therapy works for teens because the space is theirs. We'll share general themes and progress with you, guide you on supporting your teen at home, and involve you immediately if there's ever a safety concern."],
  ],
  "child-therapy": [
    ["For parents", "You stay involved in the ways that help — the initial intake, periodic check-ins, and strategies to support progress at home — while your child gets a space that belongs to them."],
  ],
  "family-counselling": [
    ["For every member of the family", "Each voice gets heard. No one is treated as the problem — the pattern is."],
  ],
  "parent-support-coaching": [
    ["For parents", "Coaching here is judgment-free. You leave with strategies that fit your child and your household — not criticism."],
  ],
};

const SIGNS = {
  "family-counselling": [
    "Conversations escalate into arguments or silence before anything gets resolved",
    "One member's behaviour or distress is affecting the whole household",
    "You're navigating a transition — separation, remarriage, blending families, or loss",
    "You're rebuilding after estrangement or a rupture in trust",
    "Roles, rules, or expectations are creating ongoing tension at home",
  ],
};

const FAQS = {
  "family-counselling": [
    ["How many sessions will we need?", "It depends on your family's goals. Many families notice meaningful change within 8 to 12 sessions; treatment plans are reviewed together as you progress."],
    ["Can sessions be virtual?", "In-person sessions at our Milton and Burlington clinics are often most effective for families, but secure virtual sessions are available across Ontario when needed."],
    ["What if one family member doesn't want to participate?", "That happens often. Work can still be effective when willing members begin — others frequently choose to join once the process feels safer."],
    ["Is family counselling covered by insurance?", "Psychotherapy provided by a Registered Psychotherapist (Qualifying) is covered under many extended health benefit plans. Receipts are provided for reimbursement."],
  ],
};

export default function Concern() {
  const { slug } = useParams();
  const c = CONCERNS[slug];

  useEffect(() => {
    if (!c) return;
    document.title = `${c.title} in Milton & Burlington | Haya Healing Psychotherapy`;
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", `${c.title} with Momna Sajid, Registered Psychotherapist (Qualifying). Evidence-based care in English & Urdu — in person in Milton & Burlington, virtual across Ontario.`);
  }, [c]);
  if (!c) {
    return (
      <div className="min-h-screen">
        <SiteHeader />
        <div className="px-6 md:px-14 py-32 max-w-2xl">
          <p className="eyebrow">Area of concern</p>
          <h1 className="font-serif-display text-4xl mt-4">Page not found.</h1>
          <a href="/#services" className="pill-btn pill-outline mt-8">View all areas of concern</a>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen">
      <SiteHeader />
      <section className="px-6 md:px-14 pt-14 md:pt-20 pb-12 max-w-4xl">
        <motion.a variants={fadeUp} initial="hidden" animate="visible" custom={0} href="/#services" className="inline-flex items-center gap-2 text-sm text-[#2c1f20]/60 hover:text-[#6e4550] transition-colors">
          <ArrowLeft size={14} /> All areas of concern
        </motion.a>
        <motion.p variants={fadeUp} initial="hidden" animate="visible" custom={1} className="eyebrow mt-8">Psychotherapy · Milton, Burlington &amp; Online Across Ontario</motion.p>
        <motion.h1 variants={fadeUp} initial="hidden" animate="visible" custom={2} data-testid="concern-heading"
          className="font-serif-display mt-4 text-[clamp(2.2rem,5vw,4rem)] leading-[1.08] font-normal">
          {c.title}
        </motion.h1>
        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={3} className="mt-6 space-y-4 text-[#2c1f20]/75 leading-relaxed max-w-2xl">
          {c.intro.map((p) => <p key={p}>{p}</p>)}
        </motion.div>
        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={4} className="mt-8">
          <a data-testid="concern-book-button" href={JANE_URL} target="_blank" rel="noreferrer" className="pill-btn pill-solid">Book Initial Consultation <ArrowRight size={15} /></a>
        </motion.div>
      </section>
      <section data-testid="concern-bullets" className="px-6 md:px-14 py-20 md:py-24 border-t border-[#2c1f20]/10">
        <Reveal><p className="eyebrow">Support may focus on</p></Reveal>
        <div className="grid sm:grid-cols-2 gap-x-12 gap-y-3.5 mt-8 max-w-4xl">
          {c.bullets.map((b, i) => (
            <Reveal key={b} i={i % 4} className="flex gap-3 text-[#2c1f20]/80">
              <span className="text-[#8a5a63] mt-0.5">✦</span><span>{b}</span>
            </Reveal>
          ))}
        </div>
      </section>
      {SIGNS[slug] && (
        <section data-testid="concern-signs" className="px-6 md:px-14 py-20 md:py-24 border-t border-[#2c1f20]/10 bg-[#f1ece4]/50">
          <Reveal><p className="eyebrow">Is this right for your family?</p></Reveal>
          <Reveal i={1}><h2 className="font-serif-display text-3xl md:text-4xl mt-4 max-w-2xl leading-tight">How to know if <em className="text-[#6e4550]">{c.title.toLowerCase()}</em> could help.</h2></Reveal>
          <p className="mt-6 text-[#2c1f20]/75 leading-relaxed max-w-2xl">Support isn't only for moments of crisis. It can help during everyday strain, transitions, or times when relationships simply feel harder than they used to. It may be a good fit if:</p>
          <div className="grid sm:grid-cols-2 gap-x-12 gap-y-3.5 mt-8 max-w-4xl">
            {SIGNS[slug].map((s, i) => (
              <Reveal key={s} i={i % 4} className="flex gap-3 text-[#2c1f20]/80">
                <span className="text-[#8a5a63] mt-0.5">✦</span><span>{s}</span>
              </Reveal>
            ))}
          </div>
        </section>
      )}
      <section data-testid="concern-benefits" className="px-6 md:px-14 py-20 md:py-24 border-t border-[#2c1f20]/10">
        <Reveal><p className="eyebrow">What changes</p></Reveal>
        <Reveal i={1}><h2 className="font-serif-display text-3xl md:text-4xl mt-4 max-w-2xl leading-tight">How {c.title} <em className="text-[#6e4550]">can help you.</em></h2></Reveal>
        <div className="grid sm:grid-cols-3 gap-6 mt-12">
          {(BENEFITS[slug] || DEFAULT_BENEFITS).map(([t, d], i) => (
            <Reveal key={t} i={i + 2} className="card">
              <h3 className="font-serif-display text-xl md:text-2xl leading-snug">{t}</h3>
              <p className="text-sm text-[#2c1f20]/70 mt-2 leading-relaxed">{d}</p>
            </Reveal>
          ))}
        </div>
      </section>
      {AUDIENCE_NOTES[slug] && (
        <section data-testid="concern-audience" className="px-6 md:px-14 py-20 md:py-24 border-t border-[#2c1f20]/10">
          <div className={`grid gap-6 max-w-4xl ${AUDIENCE_NOTES[slug].length > 1 ? "md:grid-cols-2" : ""}`}>
            {AUDIENCE_NOTES[slug].map(([label, text], i) => (
              <Reveal key={label} i={i} className="card">
                <p className="eyebrow">{label}</p>
                <p className="mt-4 text-sm text-[#2c1f20]/75 leading-relaxed">{text}</p>
              </Reveal>
            ))}
          </div>
        </section>
      )}
      <section className="border-y border-[#2c1f20]/10">
        <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#2c1f20]/10">
          {[["English & Urdu", "Move between languages whenever it helps"], ["In-person", "Halton Medix Walk-In Clinic — Milton & Burlington"], ["Virtual", "Across Ontario, on a secure PHIPA-compliant platform"]].map(([t, d]) => (
            <div key={t} className="py-7 px-6 text-center">
              <p className="font-serif-display text-2xl text-[#6e4550]">{t}</p>
              <p className="text-xs text-[#2c1f20]/55 mt-1">{d}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="px-6 md:px-14 py-10 border-t border-[#2c1f20]/10">
        <p className="text-xs text-[#2c1f20]/50 leading-relaxed max-w-5xl">
          {c.title} at Haya Healing Psychotherapy — serving Milton, Burlington, Oakville, Mississauga, Brampton, Halton Region, and the Greater Toronto Area. Momna Sajid, Registered Psychotherapist (Qualifying), CRPO. Sessions in English and Urdu. In-person at Halton Medix Walk-In Clinic (Milton & Burlington) and virtual across Ontario.
        </p>
      </section>
      {FAQS[slug] && (
        <section data-testid="concern-faq" className="px-6 md:px-14 py-20 md:py-24 border-t border-[#2c1f20]/10">
          <Reveal><p className="eyebrow">Common questions</p></Reveal>
          <Reveal i={1}><h2 className="font-serif-display text-3xl md:text-4xl mt-4 max-w-2xl leading-tight">Questions families <em className="text-[#6e4550]">often ask.</em></h2></Reveal>
          <div className="grid md:grid-cols-2 gap-6 mt-12 max-w-5xl">
            {FAQS[slug].map(([q, a], i) => (
              <Reveal key={q} i={i % 4} className="card">
                <h3 className="font-serif-display text-xl leading-snug">{q}</h3>
                <p className="text-sm text-[#2c1f20]/70 mt-2 leading-relaxed">{a}</p>
              </Reveal>
            ))}
          </div>
        </section>
      )}
      <section data-testid="concern-differentiators" className="px-6 md:px-14 py-20 md:py-24 border-t border-[#2c1f20]/10 bg-[#f1ece4]/50">
        <Reveal><p className="eyebrow">Why Haya Healing Psychotherapy</p></Reveal>
        <Reveal i={1}><h2 className="font-serif-display text-3xl md:text-4xl mt-4 max-w-2xl leading-tight">Care that is <em className="text-[#6e4550]">evidence-based</em> and culturally informed.</h2></Reveal>
        <div className="grid sm:grid-cols-2 gap-6 mt-12 max-w-4xl">
          {[
            ["Evidence-Based Care", "Treatment is grounded in validated approaches — CBT, ACT, EFT, and trauma-informed practice — matched to your goals and reviewed as you progress."],
            ["Culturally Responsive", "Your culture, faith, family system, and migration history are treated as clinical context — never as an afterthought."],
          ].map(([t, d], i) => (
            <Reveal key={t} i={i + 2} className="card">
              <h3 className="font-serif-display text-xl md:text-2xl leading-snug">{t}</h3>
              <p className="text-sm text-[#2c1f20]/70 mt-2 leading-relaxed">{d}</p>
            </Reveal>
          ))}
        </div>
      </section>
      <footer className="px-6 md:px-14 py-16 md:py-24 bg-[#f1ece4]/50">
        <Reveal><p className="eyebrow">Begin here</p></Reveal>
        <Reveal i={1}><h2 className="font-serif-display text-4xl md:text-5xl mt-4 max-w-2xl leading-tight">Book an <em className="text-[#6e4550]">Initial Consultation.</em></h2></Reveal>
        <Reveal i={2} className="mt-8 flex flex-wrap gap-6 items-center">
          <a data-testid="concern-footer-book" href={JANE_URL} target="_blank" rel="noreferrer" className="pill-btn pill-solid">Book via Jane App <ArrowUpRight size={15} /></a>
          <a href="mailto:momna@hayahealingpsychotherapy.com" className="flex items-center gap-2 text-sm text-[#2c1f20]/75 hover:text-[#6e4550] transition-colors"><Mail size={15} /> momna@hayahealingpsychotherapy.com</a>
          <a href="tel:+13656172135" className="flex items-center gap-2 text-sm text-[#2c1f20]/75 hover:text-[#6e4550] transition-colors"><Phone size={15} /> (365) 617-2135</a>
        </Reveal>
        <p className="mt-14 text-xs text-[#2c1f20]/40 flex items-center gap-2"><MapPin size={12} /> Milton · Burlington · Virtual across Ontario</p>
        <p className="mt-4 text-[8px] leading-relaxed text-[#2c1f20]/30">Some content on this site was created with the assistance of AI tools and reviewed by Haya Healing Psychotherapy.</p>
      </footer>
    </div>
  );
}
